<template>
  <v-card class="filter-panel pa-4" width="300">
    <!-- FT-143: панель фильтров клиентов по образцу CarsFilterPanel -->
    <v-select v-model="local.entity" :items="typeItems" :label="$t('clients_list.type')" clearable
      hide-details dense prepend-inner-icon="mdi-account-multiple-outline" class="mb-4" @change="emitNow" />

    <v-select v-model="local.debtor" :items="debtorItems" :label="$t('custom.debts_clients')" clearable
      hide-details dense prepend-inner-icon="mdi-cash-remove" class="mb-4" @change="emitNow" />

    <v-select v-model="local.category" :items="categoryItems" :label="$t('clients.category')" multiple
      clearable hide-details dense prepend-inner-icon="mdi-account-star-outline" class="mb-4" @change="emitNow">
      <template v-slot:selection="{ item, index }">
        <span v-if="index === 0" class="text-truncate">{{ item.text }}</span>
        <span v-if="index === 1" class="grey--text text-caption ml-1">+{{ local.category.length - 1 }}</span>
      </template>
    </v-select>

    <!-- источники — свободные строки, заполняемые пользователями; опции отдаёт clients#list -->
    <v-autocomplete v-model="local.source" :items="sources" :label="$t('custom.source')" multiple
      clearable hide-details dense prepend-inner-icon="mdi-source-branch" class="mb-4" @change="emitNow">
      <template v-slot:selection="{ item, index }">
        <span v-if="index === 0" class="text-truncate">{{ item }}</span>
        <span v-if="index === 1" class="grey--text text-caption ml-1">+{{ local.source.length - 1 }}</span>
      </template>
    </v-autocomplete>

    <v-select v-model="local.company_id" :items="branchItems" :label="$t('custom.branch')" clearable
      hide-details dense prepend-inner-icon="mdi-office-building-marker-outline" class="mb-4" @change="emitNow" />

    <v-checkbox v-model="birthdayProxy" :label="$t('clients_list.birthday_today')" hide-details dense
      class="mt-0 mb-2" @change="emitNow" />

    <!-- пресеты фильтров (localStorage, как у машин/броней) -->
    <v-divider class="my-4"></v-divider>
    <div v-if="presets.length" class="d-flex flex-wrap align-center mb-3" style="gap: 6px">
      <v-icon small class="mr-1">mdi-bookmark-outline</v-icon>
      <v-chip v-for="(p, i) in presets" :key="i" small close @click="applyPreset(p)" @click:close="deletePreset(i)">
        {{ p.name }}
      </v-chip>
    </div>
    <div class="d-flex align-center" style="gap: 8px">
      <v-text-field v-model="presetName" :label="$t('cars.name')" hide-details dense
        @keyup.enter="savePreset" />
      <v-btn small color="primary" :disabled="!presetName.trim()" @click="savePreset">
        <v-icon left small>mdi-content-save-outline</v-icon>{{ $t("custom.save") }}
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import { categoryText, emptyFilters } from "@/components/dashboard/clients/clientsFilterChips";

const CATEGORY_VALUES = ["Новый", "Лояльный", "Постоянный", "Серый", "Чёрный"];

export default {
  name: "ClientsFilterPanel",
  props: {
    value: { type: Object, required: true },
    // distinct-источники компании (из ответа clients#list)
    sources: { type: Array, default: () => [] },
    // филиалы: [{ id, city, name }] — уже загруженные страницей /main_companies
    branches: { type: Array, default: () => [] },
    presetsKey: { type: String, default: "clients_filter_presets" },
  },
  data() {
    return { local: { ...this.value }, presets: [], presetName: "" };
  },
  created() {
    this.presets = this.loadPresets();
  },
  computed: {
    typeItems() {
      return [
        { text: this.$t("clients_list.persons"), value: false },
        { text: this.$t("clients_list.entities"), value: true },
      ];
    },
    debtorItems() {
      return [
        { text: this.$t("custom.yes"), value: true },
        { text: this.$t("custom.no"), value: false },
      ];
    },
    categoryItems() {
      return CATEGORY_VALUES.map((v) => ({ text: categoryText(this.$t.bind(this), v), value: v }));
    },
    branchItems() {
      return this.branches.map((b) => ({ text: b.city || b.name || String(b.id), value: b.id }));
    },
    birthdayProxy: {
      get() { return this.local.birthday_today === true; },
      set(v) { this.local.birthday_today = v ? true : false; },
    },
  },
  watch: {
    value(v) { this.local = { ...v }; },
  },
  methods: {
    emitNow() { this.$emit("input", { ...this.local }); },
    loadPresets() {
      try {
        const arr = JSON.parse(localStorage.getItem(this.presetsKey) || "[]");
        if (!Array.isArray(arr)) return [];
        return arr.filter((p) => p && typeof p.name === "string" && p.filters);
      } catch (e) {
        return [];
      }
    },
    persistPresets() {
      try {
        localStorage.setItem(this.presetsKey, JSON.stringify(this.presets));
      } catch (e) {
        // storage недоступен/полон — держим в памяти, игнор
      }
    },
    // состав пресета — все поля фильтров без поиска (спека REQ-02)
    presetFilters(f) {
      return {
        entity: f.entity === true || f.entity === false ? f.entity : null,
        debtor: f.debtor === true || f.debtor === false ? f.debtor : null,
        category: f.category || [],
        source: f.source || [],
        company_id: f.company_id || null,
        birthday_today: f.birthday_today === true,
      };
    },
    savePreset() {
      const name = this.presetName.trim();
      if (!name) return;
      const preset = { name, filters: this.presetFilters(this.local) };
      const idx = this.presets.findIndex((p) => p.name === name);
      if (idx >= 0) this.presets.splice(idx, 1, preset);
      else this.presets.push(preset);
      this.persistPresets();
      this.presetName = "";
    },
    applyPreset(p) {
      this.local = { ...emptyFilters(), search: this.local.search, ...this.presetFilters(p.filters || {}) };
      this.emitNow();
    },
    deletePreset(i) {
      this.presets.splice(i, 1);
      this.persistPresets();
    },
  },
};
</script>
